import Axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';

import config from '@/config';
import { useAccountStore } from '@/store/use-account-store';
import { ErrorCode, EventType, getErrorMsg } from '@/types/enums';
import { sendEvent } from '@/utils/analytics';

type ApiClient = Omit<
  AxiosInstance,
  'get' | 'post' | 'put' | 'delete' | 'patch'
> & {
  get<T = any>(url: string, config?: any): Promise<T>;
  post<T = any>(url: string, data?: any, config?: any): Promise<T>;
  put<T = any>(url: string, data?: any, config?: any): Promise<T>;
  delete<T = any>(url: string, config?: any): Promise<T>;
  patch<T = any>(url: string, data?: any, config?: any): Promise<T>;
};

const REST_URL = import.meta.env.VITE_REST_URL;
const REST_V2_URL = import.meta.env.VITE_REST_V2_URL;
const REST_ORDERS_URL = import.meta.env.VITE_REST_ORDERS_URL;
const COOKIE_AWARE_REST_V2_URL = import.meta.env.DEV
  ? REST_V2_URL
  : import.meta.env.VITE_ENV === 'development'
    ? '/dev/api'
    : import.meta.env.VITE_ENV === 'staging'
      ? '/staging/api'
      : '/api';

export const publicApi: ApiClient = Axios.create({
  baseURL: REST_URL,
  // headers: {
  //   common: {
  //     xrestservermm: `restserver${Math.floor(Math.random() * (import.meta.env.VITE_ENV === 'production' ? 6 : 2))}`,
  //   },
  // },
});

export const publicV2Api: ApiClient = Axios.create({
  baseURL: REST_V2_URL,
});

export const privateApi: ApiClient = Axios.create({
  baseURL: REST_URL,
});

export const privateV2Api: ApiClient = Axios.create({
  baseURL: REST_V2_URL,
});

export const primaryPrivateApi: ApiClient = Axios.create({
  baseURL: REST_URL,
});

export const primaryPrivateV2Api: ApiClient = Axios.create({
  baseURL: REST_V2_URL,
});

export const ordersApi: ApiClient = Axios.create({
  baseURL: config.isProduction && !config.isLocal ? '/api' : REST_ORDERS_URL, // uses reverse proxy on production
});

export const cookieAwarePublicV2Api: ApiClient = Axios.create({
  baseURL: COOKIE_AWARE_REST_V2_URL,
});

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  if (config.headers) {
    config.headers.Accept = 'application/json';
  }

  const { getAuthParams } = useAccountStore.getState();
  const authParams = getAuthParams();

  if (authParams?.apiKey && config.headers && !config.headers['X-API-KEY']) {
    config.headers['X-API-KEY'] = authParams.apiKey;
  }

  return config;
}
publicApi.interceptors.request.use(authRequestInterceptor);
publicV2Api.interceptors.request.use(authRequestInterceptor);
privateApi.interceptors.request.use(authRequestInterceptor);
privateV2Api.interceptors.request.use(authRequestInterceptor);
ordersApi.interceptors.request.use(authRequestInterceptor);
cookieAwarePublicV2Api.interceptors.request.use(authRequestInterceptor);

async function signatureInterceptor(config: InternalAxiosRequestConfig) {
  const method = config.method?.toUpperCase();
  if (method === 'GET') {
    // const { authParams } = useAccountStore.getState();
    // if (!authParams) {
    //   throw new Error('No auth params');
    // }
    // const path = config.url || '';
    const time = useAccountStore.getState().getAccurateTime();
    try {
      // const signature = await signMessage({
      //   message: `${path},time=${time}`,
      //   privateKey: authParams.signingKey,
      // });
      config.params = {
        ...config.params,
        // signature,
        time,
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
  return config;
}
privateApi.interceptors.request.use(signatureInterceptor);
privateV2Api.interceptors.request.use(signatureInterceptor);
primaryPrivateApi.interceptors.request.use(signatureInterceptor);
primaryPrivateV2Api.interceptors.request.use(signatureInterceptor);
ordersApi.interceptors.request.use(signatureInterceptor);

function responseInterceptor(response: AxiosResponse) {
  return response.data;
}

function errorInterceptor(error: AxiosError) {
  console.error(error);
  return Promise.reject(error);
}

type ErrorResponse =
  | {
      detail: {
        code: ErrorCode;
        msg: string;
      };
    }
  | {
      code: ErrorCode;
      msg: string;
    };

export const getErrorMsgByResponse = (
  error: any,
  t: (key: string) => string,
): string => {
  if (typeof error === 'number') {
    return ErrorCode[error] ? getErrorMsg(error, t) : error.toString();
  }

  if (typeof error === 'string') {
    return error;
  }

  const data = error?.response?.data as ErrorResponse | undefined;
  const code = data
    ? 'detail' in data
      ? data.detail.code
      : data.code
    : undefined;
  const msg = data
    ? 'detail' in data
      ? data.detail.msg
      : data.msg
    : undefined;

  if (code !== undefined && ErrorCode[code]) {
    return getErrorMsg(code, t);
  }

  if (msg) {
    return msg;
  }

  const fallbackDetail = (error as AxiosError<{ detail: string }>)?.response
    ?.data?.detail;
  const fallbackMessage = (error as Error)?.message;

  return fallbackDetail ?? fallbackMessage ?? 'Unknown error';
};

export const getPrivateApiErrorInterceptor =
  (options?: { isPrimary?: boolean }) => (error: AxiosError) => {
    console.error(error);

    const data = error.response?.data as ErrorResponse | undefined;
    const code = data
      ? 'detail' in data
        ? data.detail.code
        : data.code
      : undefined;
    const msg = data
      ? 'detail' in data
        ? data.detail.msg
        : data.msg
      : undefined;

    const request = error.request as XMLHttpRequest | undefined;

    sendEvent(EventType.REQUEST_FAILED, {
      code,
      msg,
      url: request?.responseURL,
    });

    if (code) {
      if (code === ErrorCode.UNAUTHORIZED) {
        if (options?.isPrimary) {
          useAccountStore.setState({ hasPrimaryAuth: false });
          clearPrimaryPrivateApiHeaders(['X-API-KEY']);
        } else {
          useAccountStore.setState({ hasAuth: false });
          clearPrivateApiHeaders(['X-API-KEY']);
        }
      } else if (ErrorCode[code]) {
        return Promise.reject(code);
      } else if (msg) {
        toast.error(msg);
      }

      return Promise.reject(ErrorCode.UNKNOWN);
    } else {
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Not authenticated
        if (options?.isPrimary) {
          useAccountStore.setState({ hasPrimaryAuth: false });
          clearPrimaryPrivateApiHeaders(['X-API-KEY']);
        } else {
          useAccountStore.setState({ hasAuth: false });
          clearPrivateApiHeaders(['X-API-KEY']);
        }
        return Promise.reject(ErrorCode.UNAUTHORIZED);
      } else if (msg) {
        toast.error(msg);
      }

      return Promise.reject(ErrorCode.UNKNOWN);
    }
  };
publicApi.interceptors.response.use(responseInterceptor, errorInterceptor);
publicV2Api.interceptors.response.use(responseInterceptor, errorInterceptor);
privateApi.interceptors.response.use(
  responseInterceptor,
  getPrivateApiErrorInterceptor(),
);
privateV2Api.interceptors.response.use(
  responseInterceptor,
  getPrivateApiErrorInterceptor(),
);
primaryPrivateApi.interceptors.response.use(
  responseInterceptor,
  getPrivateApiErrorInterceptor({ isPrimary: true }),
);
primaryPrivateV2Api.interceptors.response.use(
  responseInterceptor,
  getPrivateApiErrorInterceptor({ isPrimary: true }),
);
ordersApi.interceptors.response.use(
  responseInterceptor,
  getPrivateApiErrorInterceptor(),
);
cookieAwarePublicV2Api.interceptors.response.use(
  responseInterceptor,
  errorInterceptor,
);

export function updatePrivateApiHeaders(headers: Record<string, string>) {
  Object.entries(headers).forEach(([key, value]) => {
    privateApi.defaults.headers.common[key] = value;
    privateV2Api.defaults.headers.common[key] = value;
    ordersApi.defaults.headers.common[key] = value;
  });
}

export function updatePrimaryPrivateApiHeaders(
  headers: Record<string, string>,
) {
  Object.entries(headers).forEach(([key, value]) => {
    primaryPrivateApi.defaults.headers.common[key] = value;
    primaryPrivateV2Api.defaults.headers.common[key] = value;
  });
}

export function clearPrivateApiHeaders(headers: string[]) {
  headers.forEach((header) => {
    delete privateApi.defaults.headers.common[header];
    delete privateV2Api.defaults.headers.common[header];
    delete ordersApi.defaults.headers.common[header];
  });
}

export function clearPrimaryPrivateApiHeaders(headers: string[]) {
  headers.forEach((header) => {
    delete primaryPrivateApi.defaults.headers.common[header];
    delete primaryPrivateV2Api.defaults.headers.common[header];
  });
}
